// Rolls loot for an encounter from a campaign's loot tables.
//
// Coins and items: entries only count if they fit the encounter: its difficulty, the highest CR of the creatures in it,
// its Environments, and its creatures. Guaranteed entries that fit are each given once,
// and then each roll picks one of the other entries that fit, by weight.
// An entry gives its money and each of its item groups, which can be items picked at random
// from a limitation, or a specific item.
//
// Crafting materials are rolled separately, for each creature the party faced:
// every material entry that fits that creature has its own chance to give some of its material.
//
// Matching creatures and finding items need the api, so those are passed in,
// and everything else works on plain data so it can be tested.

import { EncounterDifficulty } from "./encounter_generator";

export const LOOT_ROLLS_BY_DIFFICULTY: { [difficulty in EncounterDifficulty]: number } = {
  Easy: 1,
  Medium: 2,
  Hard: 3,
  Deadly: 4,
};

export interface DiceLike {
  size: number; // 1 means count is a flat number
  count: number;
  flat?: number; // Added to what's rolled
}

export interface ItemGroupLike<I> {
  source: "Items" | "Specific Item";
  count: DiceLike;
  item_limit: I;
  min_value: number;
  max_value: number;
  item_id: string;
}

export interface LootEntryLike<C, I> {
  true_id: string;
  name: string;
  weight: number;
  guaranteed: boolean;
  environment_ids: string[];
  difficulties: string[];
  min_cr: number;
  max_cr: number;
  creature_limit: C;
  item_groups: ItemGroupLike<I>[];
  money: { pp: DiceLike, gp: DiceLike, ep: DiceLike, sp: DiceLike, cp: DiceLike };
}

export interface LootLookups<I> {
  // The items a group can give (Items groups already filtered by value)
  itemsFor: (group: ItemGroupLike<I>) => { id: string, name: string }[];
}

export interface LootSource<C, I> {
  table_id: string;
  table_name: string;
  entries: LootEntryLike<C, I>[];
}

export interface LootContext<C> {
  difficulty: EncounterDifficulty;
  environment_ids: string[];
  known_environment_ids: string[]; // All Environments that exist, so leftover values can be ignored
  max_cr: number; // Highest CR of the creatures in the encounter
  creatureMatches: (limit: C) => boolean; // Whether any creature in the encounter fits (true for an empty limit)
}

export interface LootCandidate<C, I> {
  table_id: string;
  table_name: string;
  entry: LootEntryLike<C, I>;
}

export interface RolledItem {
  id: string; // PseudoItem id
  name: string;
  count: number;
}

export interface RolledMoney {
  pp: number;
  gp: number;
  ep: number;
  sp: number;
  cp: number;
}

export interface RolledLoot {
  key: string;
  table_id: string;
  table_name: string;
  entry_id: string;
  entry_name: string;
  guaranteed: boolean;
  items: RolledItem[];
  money: RolledMoney;
  problems: string[]; // Things that couldn't be given, like items nothing fits
}

export const rollDice = (dice: DiceLike, rng: () => number): number => {
  const flat = dice.flat ?? 0;
  if (dice.size === 1) {
    return dice.count + flat;
  }
  const count = Math.abs(dice.count);
  let total = 0;
  for (let i = 0; i < count; ++i) {
    total += Math.floor(rng() * dice.size) + 1;
  }
  return (dice.count < 0 ? -total : total) + flat;
};

export const entryFits = <C, I>(entry: LootEntryLike<C, I>, ctx: LootContext<C>): boolean => {
  if (entry.weight <= 0 && !entry.guaranteed) {
    return false;
  }
  if (entry.difficulties.length > 0 && !entry.difficulties.includes(ctx.difficulty)) {
    return false;
  }
  if (entry.min_cr >= 0 && ctx.max_cr < entry.min_cr) {
    return false;
  }
  if (entry.max_cr >= 0 && ctx.max_cr > entry.max_cr) {
    return false;
  }
  if (ctx.environment_ids.length > 0) {
    // Entries without any Environments fit anywhere
    const known = entry.environment_ids.filter(o => ctx.known_environment_ids.includes(o));
    if (known.length > 0 && !known.some(o => ctx.environment_ids.includes(o))) {
      return false;
    }
  }
  return ctx.creatureMatches(entry.creature_limit);
};

export const fittingEntries = <C, I>(sources: LootSource<C, I>[], ctx: LootContext<C>): LootCandidate<C, I>[] => {
  const candidates: LootCandidate<C, I>[] = [];
  for (const source of sources) {
    for (const entry of source.entries) {
      if (entryFits(entry, ctx)) {
        candidates.push({ table_id: source.table_id, table_name: source.table_name, entry });
      }
    }
  }
  return candidates;
};

export const pickWeighted = <T extends { entry: { weight: number } }>(candidates: T[], rng: () => number): T | undefined => {
  const total = candidates.reduce((sum, o) => sum + o.entry.weight, 0);
  let roll = rng() * total;
  for (const candidate of candidates) {
    roll -= candidate.entry.weight;
    if (roll < 0) {
      return candidate;
    }
  }
  return candidates[candidates.length - 1];
};

let next_key = 0;

export const rollEntry = <C, I>(
  candidate: LootCandidate<C, I>,
  lookups: LootLookups<I>,
  rng: () => number
): RolledLoot => {
  const entry = candidate.entry;
  const label = entry.name === "" ? "(Unnamed Entry)" : entry.name;
  const result: RolledLoot = {
    key: `loot_${next_key++}`,
    table_id: candidate.table_id,
    table_name: candidate.table_name,
    entry_id: entry.true_id,
    entry_name: entry.name,
    guaranteed: entry.guaranteed,
    items: [],
    money: { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 },
    problems: [],
  };
  for (const group of entry.item_groups) {
    const count = Math.max(rollDice(group.count, rng), 0);
    if (count === 0) {
      continue;
    }
    const options = lookups.itemsFor(group);
    if (options.length === 0) {
      result.problems.push(`${count} item(s) from ${label} couldn't be found, since no items fit it with these source books.`);
      continue;
    }
    for (let i = 0; i < count; ++i) {
      const option = options[Math.floor(rng() * options.length)];
      const existing = result.items.find(o => o.id === option.id);
      if (existing) {
        existing.count++;
      } else {
        result.items.push({ id: option.id, name: option.name, count: 1 });
      }
    }
  }
  const money = entry.money;
  result.money = {
    pp: Math.max(rollDice(money.pp, rng), 0),
    gp: Math.max(rollDice(money.gp, rng), 0),
    ep: Math.max(rollDice(money.ep, rng), 0),
    sp: Math.max(rollDice(money.sp, rng), 0),
    cp: Math.max(rollDice(money.cp, rng), 0),
  };
  return result;
};

// Every guaranteed entry that fits once, then rolls times on the rest.
export const rollLoot = <C, I>({
  candidates,
  rolls,
  lookups,
  rng = Math.random,
}: {
  candidates: LootCandidate<C, I>[];
  rolls: number;
  lookups: LootLookups<I>;
  rng?: () => number;
}): RolledLoot[] => {
  const results: RolledLoot[] = candidates.filter(o => o.entry.guaranteed).map(o => rollEntry(o, lookups, rng));
  const weighted = candidates.filter(o => !o.entry.guaranteed);
  if (weighted.length > 0) {
    for (let i = 0; i < rolls; ++i) {
      const candidate = pickWeighted(weighted, rng);
      if (candidate) {
        results.push(rollEntry(candidate, lookups, rng));
      }
    }
  }
  return results;
};

// A new result to replace one. Guaranteed ones roll the same entry again, the rest pick a new entry.
export const rerollLoot = <C, I>({
  result,
  candidates,
  lookups,
  rng = Math.random,
}: {
  result: RolledLoot;
  candidates: LootCandidate<C, I>[];
  lookups: LootLookups<I>;
  rng?: () => number;
}): RolledLoot | undefined => {
  if (result.guaranteed) {
    const candidate = candidates.find(o => o.table_id === result.table_id && o.entry.true_id === result.entry_id);
    return candidate ? rollEntry(candidate, lookups, rng) : undefined;
  }
  const candidate = pickWeighted(candidates.filter(o => !o.entry.guaranteed), rng);
  return candidate ? rollEntry(candidate, lookups, rng) : undefined;
};

// Adds up all the money in a set of results.
export const totalMoney = (results: RolledLoot[]): RolledMoney => {
  return results.reduce((total, o) => ({
    pp: total.pp + o.money.pp,
    gp: total.gp + o.money.gp,
    ep: total.ep + o.money.ep,
    sp: total.sp + o.money.sp,
    cp: total.cp + o.money.cp,
  }), { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 });
};

export const moneyString = (money: RolledMoney): string => {
  const parts: string[] = [];
  if (money.pp > 0) parts.push(`${money.pp} pp`);
  if (money.gp > 0) parts.push(`${money.gp} gp`);
  if (money.ep > 0) parts.push(`${money.ep} ep`);
  if (money.sp > 0) parts.push(`${money.sp} sp`);
  if (money.cp > 0) parts.push(`${money.cp} cp`);
  return parts.join(", ");
};

// ---- Crafting materials ----

export interface MaterialEntryLike<C> {
  true_id: string;
  name: string;
  chance: number; // Percent
  min_cr: number;
  max_cr: number;
  creature_limit: C;
  material_type: string;
  amount: DiceLike;
  weight: number; // Per unit
  value: number; // Per unit
}

export interface MaterialSource<C> {
  table_id: string;
  table_name: string;
  entries: MaterialEntryLike<C>[];
}

// One creature the party faced (each one of a group separately).
export interface HarvestCreature<C> {
  key: string; // Unique to this creature in the encounter
  creature_id: string;
  name: string;
  creature_type: string;
  cr: number;
  matches: (limit: C) => boolean; // Whether it fits a creature limitation (true for an empty limit)
}

export interface HarvestedMaterial {
  key: string;
  table_name: string;
  entry_id: string;
  entry_name: string;
  creature_key: string;
  creature_id: string;
  creature_name: string;
  creature_type: string;
  creature_cr: number;
  material_type: string;
  amount: number;
  weight: number;
  value: number;
}

export const materialEntryFits = <C>(entry: MaterialEntryLike<C>, creature: HarvestCreature<C>): boolean => {
  if (entry.chance <= 0) {
    return false;
  }
  if (entry.min_cr >= 0 && creature.cr < entry.min_cr) {
    return false;
  }
  if (entry.max_cr >= 0 && creature.cr > entry.max_cr) {
    return false;
  }
  return creature.matches(entry.creature_limit);
};

let next_material_key = 0;

// Rolls one material entry for one creature, ignoring its chance.
export const rollMaterial = <C>(
  entry: MaterialEntryLike<C>,
  table_name: string,
  creature: HarvestCreature<C>,
  rng: () => number
): HarvestedMaterial => ({
  key: `material_${next_material_key++}`,
  table_name,
  entry_id: entry.true_id,
  entry_name: entry.name,
  creature_key: creature.key,
  creature_id: creature.creature_id,
  creature_name: creature.name,
  creature_type: creature.creature_type,
  creature_cr: creature.cr,
  material_type: entry.material_type,
  amount: Math.max(rollDice(entry.amount, rng), 0),
  weight: entry.weight,
  value: entry.value,
});

// Every creature gets a chance at every material entry that fits it.
export const harvestMaterials = <C>({
  sources,
  creatures,
  rng = Math.random,
}: {
  sources: MaterialSource<C>[];
  creatures: HarvestCreature<C>[];
  rng?: () => number;
}): HarvestedMaterial[] => {
  const results: HarvestedMaterial[] = [];
  for (const creature of creatures) {
    for (const source of sources) {
      for (const entry of source.entries) {
        if (materialEntryFits(entry, creature) && rng() * 100 < entry.chance) {
          const material = rollMaterial(entry, source.table_name, creature, rng);
          if (material.amount > 0) {
            results.push(material);
          }
        }
      }
    }
  }
  return results;
};
